import type { Infer } from 'superstruct';
import { array, boolean, object, record, string } from 'superstruct';

/**
 * Represents a single conversion action from the Google Ads API.
 * Maps to the conversion_action resource.
 */
export const ViewGoogleAdsConversionActionDto = object({
	/** Conversion action ID from Google Ads API */
	id: string(),
	/** Human-readable name of the conversion action */
	name: string(),
	/** Status: ENABLED, REMOVED, etc. */
	status: string(),
	/** Type: WEBPAGE, PHONE_CALL, etc. */
	type: string(),
	/** Category: PURCHASE, PAGE_VIEW, etc. */
	category: string(),
	/** Origin: WEBSITE, CALL_FROM_ADS, etc. */
	origin: string(),
	/** Full resource name: customers/{customer_id}/conversionActions/{action_id} */
	resourceName: string(),
});

/**
 * Represents a conversion goal from the Google Ads API.
 * Maps to the customer_conversion_goal resource.
 * A goal groups related conversion actions by category and origin.
 */
export const ViewGoogleAdsConversionGoalDto = object({
	/** Goal identifier: {category}~{origin} */
	id: string(),
	/** Human-readable name: "{category} - {origin}" */
	name: string(),
	/** Goal category: PURCHASE, PAGE_VIEW, PHONE_CALL_LEAD, etc. */
	category: string(),
	/** Goal origin: WEBSITE, CALL_FROM_ADS, etc. */
	origin: string(),
	/** Whether this goal is biddable at account level */
	biddable: boolean(),
	/** Full resource name: customers/{customer_id}/customerConversionGoals/{category}~{origin} */
	resourceName: string(),
	/** Helps the UI enforce selection UX (one online, many offline) for Demand Gen */
	isOffline: boolean(),
	/** Array of conversion actions that belong to this goal */
	conversionActions: array(ViewGoogleAdsConversionActionDto),
});

/**
 * Represents conversion goal selections for a single ad account.
 * Used in campaign templates to specify which goals should be biddable.
 */
export const GoogleAdsConversionGoalDto = object({
	/** Online goal selection - only one category allowed for Demand Gen */
	online: object({
		/** Selected goal category (e.g., "PURCHASE", "PAGE_VIEW") */
		category: string(),
		/** Array of selected conversion action IDs (resourceName or id) */
		actions: array(string()),
	}),
	/** Offline goal selections - array of goal keys in format "category~origin" */
	offline: array(string()),
});

/**
 * Form data structure for conversion goals across multiple ad accounts.
 * Key: avbId (ad account identifier)
 * Value: GoogleAdsConversionGoalDto (selections for that account)
 */
export const GoogleAdsFormConversionGoalDto = record(
	string(),
	GoogleAdsConversionGoalDto
);

export function hasGoogleAdsConversionGoalSelection(
	goal: Partial<GoogleAdsConversionGoalDto> | null | undefined
) {
	return Boolean(
		(goal?.online?.category && goal?.online?.actions?.length) ||
		goal?.offline?.length
	);
}

export type ViewGoogleAdsConversionActionDto = Infer<
	typeof ViewGoogleAdsConversionActionDto
>;
export type ViewGoogleAdsConversionGoalDto = Infer<
	typeof ViewGoogleAdsConversionGoalDto
>;
export type GoogleAdsConversionGoalDto = Infer<
	typeof GoogleAdsConversionGoalDto
>;
export type GoogleAdsFormConversionGoalDto = Infer<
	typeof GoogleAdsFormConversionGoalDto
>;
